import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SUBSCRIBERS from "../api/services/SUBSCRIBERS";
import Search from "../components/forms/Search";
import Table from "../components/table/Table";
import TableHead from "../components/table/TableHead";
import TableRowSubscriber from "../features/subscribers/components/TableRowSubscriber";
import Loader from "../components/ui/Loader";
import Layout from "../layouts/Layout";
import styles from "../styles/_screens/suppliers.module.scss";
import { isTokenExpired } from "../utils/isTokenExpired";
import Spinner from "../components/ui/Spinner";
import Modal from "../components/ui/Modal";

const Subscribers = () => {
  let formdata = new FormData();

  const navigate = useNavigate();

  const SUBSCRIBER_TABLE_HEAD = [
    // "#",
    "שם מנוי",
    "טלפון",
    "חיפושים",
    "מייל",
    "סטטוס",
    "סוג מנוי",
    "סוג לקוח",
    "נוצר בתאריך",
    "תאריך סיום חבילה",
    "השהייה",
    "",
  ];

  const [totalSubscribers, setTotalSubscribers] = useState(0);

  const [subscribers, setSubscribers] = useState();

  const [isUpdated, setIsUpdated] = useState(false);

  const [searchValue, setSearchValue] = useState("");

  const [deleteSuccess, setDeleteSuccess] = useState(null);

  const [filterValue, setFilterValue] = useState("");

  const [isSubDltModalOpen, setIsSubDltModalOpen] = useState(false);

  const [dltSubLoading, setDltSubLoading] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [hasMore, setHasMore] = useState(true);

  const [localSearchValue, setLocalSearchValue] = useState("");

  const [stats, setStats] = useState({
    total: 0,
    private: 0,
    business: 0,
    active: 0,
    inactive: 0,
    privateActive: 0,
    businessActive: 0,
    withSubscription: 0,
    expiringSoon: 0,
  });

  const [expiringFilter, setExpiringFilter] = useState(false);

  // fetch statistics
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await SUBSCRIBERS.get_stats();
        if (res.result) {
          setStats(res.result);
        }
      } catch (error) {
        console.log("Error fetching stats:", error);
      }
    };

    fetchStats();
  }, [isUpdated, deleteSuccess]);

  // fetch subscribers list
  useEffect(() => {
    setDeleteSuccess(null);
    setPage(1);
    setSubscribers([]);
    setHasMore(true);
  }, [filterValue, isUpdated, deleteSuccess]);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasMore && page > 1) return;

      setIsLoading(true);
      try {
        console.log("Fetching page:", page, "with filter:", filterValue);
        const pageSize = 1500;
        const res = await SUBSCRIBERS.get(page, pageSize, filterValue);
        console.log("API Response:", res);

        if (!res.result) {
          setSubscribers([]);
          setTotalSubscribers(0);
          setHasMore(false);
          return;
        }

        setTotalSubscribers(res.result.totalSubscribers);

        if (!res.result.records || res.result.records.length === 0) {
          console.log("No more records");
          setHasMore(false);
          if (page === 1) {
            setSubscribers([]);
          }
        } else {
          console.log("Loaded items:", res.result.records.length);
          let sortedItems = res.result.records.sort((a, b) =>
            a.name > b.name ? 1 : -1
          );

          setSubscribers((prev) => {
            if (page === 1) return sortedItems;
            return [...prev, ...sortedItems];
          });

          // Check if we have more data
          if (res.result.records.length < pageSize) {
            setHasMore(false);
          }
        }
      } catch (error) {
        console.log("API Error:", error);
        console.log("Error response:", error.response);
        toast.error(error.response?.data?.message || "An error occurred");
        if (error.response?.data) {
          isTokenExpired(
            error.response.data.status,
            error.response.data.message,
            navigate
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [page, filterValue, navigate]);

  // search
  const searchSubmit = async (e) => {
    e.preventDefault();

    if (searchValue.trim()) {
      try {
        const res = await SUBSCRIBERS.search(searchValue);
        if (
          !res.result ||
          !res.result.records ||
          res.result.records.length === 0
        ) {
          setSubscribers([]);
        } else {
          setSubscribers(res.result.records);
        }
        setSearchValue("");
      } catch (error) {
        console.log(error);
        toast.error(error.response?.data?.message || "An error occurred");
        if (error.response?.data) {
          isTokenExpired(
            error.response.data.status,
            error.response.data.message,
            navigate
          );
        }
      }
    }
  };

  const handleSubscriberTypeFilter = (value) => {
    console.log("Setting filter value to:", value);
    setFilterValue(value);
  };

  const loadMore = () => {
    if (!isLoading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  // Filter subscribers locally based on search and expiring filter
  const filteredSubscribers = subscribers?.filter((sub) => {
    // Apply expiring filter first
    if (expiringFilter) {
      if (!sub.lastSubscription?.endsAt) return false;
      const daysLeft = Math.ceil(
        (new Date(sub.lastSubscription.endsAt) - new Date()) /
          (1000 * 60 * 60 * 24)
      );
      if (!(daysLeft > 0 && daysLeft <= 7)) return false;
    }

    // Then apply search filter
    if (!localSearchValue.trim()) return true;
    const search = localSearchValue.toLowerCase();
    return (
      sub.name?.toLowerCase().includes(search) ||
      sub.email?.toLowerCase().includes(search) ||
      sub.phone?.includes(search)
    );
  });

  // delete subscriber
  const deleteSubscriber = async (id) => {
    formdata.append("id", id);
    formdata.append("isDeleted", true);

    setDltSubLoading(true);
    try {
      const res = await SUBSCRIBERS.update(formdata);
      toast.success(res.message || "Subscriber deleted successfully");
      setDeleteSuccess(true);
      setIsSubDltModalOpen(false);
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || "An error occurred");
    }
    setDltSubLoading(false);
  };

  if (!subscribers) {
    return (
      <Layout>
        <div className={styles.container}>
          <Loader />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        {/* STATISTICS CARDS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "15px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "20px",
              borderRadius: "12px",
              color: "white",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontSize: "14px", opacity: 0.9 }}>סה״כ מנויים</div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", marginTop: "8px" }}
            >
              {stats.total.toLocaleString()}
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              padding: "20px",
              borderRadius: "12px",
              color: "white",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontSize: "14px", opacity: 0.9 }}>מנויים פרטיים</div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", marginTop: "8px" }}
            >
              {stats.private.toLocaleString()}
            </div>
            <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px" }}>
              {((stats.private / stats.total) * 100).toFixed(1)}% | פעילים:{" "}
              {stats.privateActive.toLocaleString()}
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              padding: "20px",
              borderRadius: "12px",
              color: "white",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontSize: "14px", opacity: 0.9 }}>מנויים עסקיים</div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", marginTop: "8px" }}
            >
              {stats.business.toLocaleString()}
            </div>
            <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px" }}>
              {((stats.business / stats.total) * 100).toFixed(1)}% | פעילים:{" "}
              {stats.businessActive.toLocaleString()}
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
              padding: "20px",
              borderRadius: "12px",
              color: "white",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontSize: "14px", opacity: 0.9 }}>פעילים</div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", marginTop: "8px" }}
            >
              {stats.active.toLocaleString()}
            </div>
            <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px" }}>
              {((stats.active / stats.total) * 100).toFixed(1)}%
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
              padding: "20px",
              borderRadius: "12px",
              color: "white",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontSize: "14px", opacity: 0.9 }}>עם מנוי פעיל</div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", marginTop: "8px" }}
            >
              {stats.withSubscription.toLocaleString()}
            </div>
            <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px" }}>
              {((stats.withSubscription / stats.total) * 100).toFixed(1)}%
            </div>
          </div>

          <div
            onClick={() => setExpiringFilter(!expiringFilter)}
            style={{
              background: expiringFilter
                ? "linear-gradient(135deg, #ff6a88 0%, #ff9a56 100%)"
                : "linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)",
              padding: "20px",
              borderRadius: "12px",
              color: "white",
              boxShadow: expiringFilter
                ? "0 6px 12px rgba(0,0,0,0.2)"
                : "0 4px 6px rgba(0,0,0,0.1)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              transform: expiringFilter ? "scale(1.05)" : "scale(1)",
            }}
          >
            <div style={{ fontSize: "14px", opacity: 0.9 }}>
              {expiringFilter ? "🔍 " : ""}פג תוקף בקרוב
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", marginTop: "8px" }}
            >
              {stats.expiringSoon.toLocaleString()}
            </div>
            <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px" }}>
              {expiringFilter ? "לחץ לביטול סינון" : "לחץ לסינון - תוך 7 ימים"}
            </div>
          </div>
        </div>

        {/* SEARCH */}
        <Search
          placeholder="חיפוש מנוי"
          mb={2}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          handleSubmit={searchSubmit}
        />
        {/* TABLE */}
        <Table
          mb={2}
          title={`רשימת מנויים (${totalSubscribers.toLocaleString()})`}
          exportBtnText="ייצוא"
          showSubscriberTypeFilter={true}
          onFilterChange={handleSubscriberTypeFilter}
          isLoading={isLoading}
          filterValue={filterValue}
          showLocalSearch={true}
          localSearchValue={localSearchValue}
          onLocalSearchChange={(e) => setLocalSearchValue(e.target.value)}
        >
          <TableHead data={SUBSCRIBER_TABLE_HEAD} />
          <tbody>
            {filteredSubscribers && filteredSubscribers.length > 0 ? (
              filteredSubscribers.map((row) => (
                <TableRowSubscriber
                  key={row._id}
                  row={row}
                  setIsUpdated={setIsUpdated}
                  isUpdated={isUpdated}
                  setIsSubDltModalOpen={setIsSubDltModalOpen}
                />
              ))
            ) : (
              <tr className={styles.emptyRow}>
                <td colSpan={7}>
                  {localSearchValue
                    ? "לא נמצאו תוצאות לחיפוש"
                    : "לא נמצאו רשומות"}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {/* LOAD MORE BUTTON */}
        {hasMore && subscribers && subscribers.length > 0 && (
          <button
            className={styles.loadMore}
            onClick={loadMore}
            disabled={isLoading}
          >
            {isLoading ? <Spinner /> : "טען עוד"}
          </button>
        )}
        {/* DELETE SUBSCRIBER MODAL */}
        {isSubDltModalOpen && (
          <Modal setIsOpen={setIsSubDltModalOpen}>
            <div className={styles.modalContainer}>
              <h1>האם אתה בטוח?</h1>
              <p>אתה בטוח שאתה רוצה למחוק את המשתמש?</p>

              <div className={styles.btnContainer}>
                <button onClick={() => setIsSubDltModalOpen(false)}>לא</button>
                <button onClick={() => deleteSubscriber(isSubDltModalOpen)}>
                  {dltSubLoading ? <Spinner /> : "כן"}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Layout>
  );
};

export default Subscribers;
